
import React from 'react';
import { SimulationConfig, RenderLayer } from '@/types';
import { InfoPopup } from './InfoPopup';

interface LayerSortingSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
}

export const LayerSortingSection: React.FC<LayerSortingSectionProps> = ({ config, setConfig }) => {
  const ALL_LAYERS: RenderLayer[] = ['WALLS', 'RINGS', 'TRAIL', 'BLAST', 'FX', 'BALL', 'HUD'];
  
  const getOrder = (order: RenderLayer[] | undefined) => {
    if (!order || order.length === 0) return ALL_LAYERS;
    // Filter out any layers that might have been removed from the type but are in saved config
    const validOrder = order.filter(l => ALL_LAYERS.includes(l));
    // Add any missing layers
    const missing = ALL_LAYERS.filter(l => !validOrder.includes(l));
    return [...validOrder, ...missing];
  };

  const moveLayer = (index: number, direction: 'UP' | 'DOWN') => {
    const newOrder = [...getOrder(config.renderOrder)];
    if (direction === 'UP') {
      if (index === 0) return;
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
    } else {
      if (index === newOrder.length - 1) return;
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
    }
    setConfig(p => ({ ...p, renderOrder: newOrder }));
  };

  const getLabel = (layer: RenderLayer) => {
      switch(layer) {
          case 'WALLS': return 'Container Walls';
          case 'RINGS': return 'Escape Rings';
          case 'TRAIL': return 'Trails & Lines';
          case 'BALL': return 'Physics Spheres';
          case 'FX': return 'Collision Ripple FX';
          case 'BLAST': return 'Broken Shards (Blast)';
          case 'HUD': return 'Heads-Up Display (HUD) UI';
          default: return layer;
      }
  };

  const currentOrder = getOrder(config.renderOrder);

  return (
    <div className="w-full flex flex-col gap-3 p-4 rounded-3xl border bg-gray-500/5 border-gray-500/20 mb-4">
      <div className="flex flex-col gap-1.5">
          <span className="text-[8px] opacity-50 text-center mb-1 uppercase font-black">Top (Front)</span>
          {[...currentOrder].reverse().map((layer, reverseIdx) => {
              const actualIdx = currentOrder.length - 1 - reverseIdx;
              return (
                  <div key={layer} className={`flex items-center justify-between p-2 rounded-xl border transition-all ${config.darkMode ? 'bg-neutral-800 border-neutral-700' : 'bg-white border-neutral-200'}`}>
                      <div className="flex items-center gap-2">
                          <span className="text-[9px] font-black uppercase">{getLabel(layer)}</span>
                          <InfoPopup settingKey={`layer_${layer}`} darkMode={config.darkMode} />
                      </div>
                      <div className="flex gap-1">
                          <button 
                            onClick={() => moveLayer(actualIdx, 'DOWN')} // Visual Up is Array End (Down index)
                            disabled={actualIdx === currentOrder.length - 1}
                            className="w-6 h-6 flex items-center justify-center rounded bg-black/10 hover:bg-black/20 disabled:opacity-20"
                          >
                              <i className="fa-solid fa-arrow-up text-[8px]"></i>
                          </button>
                          <button 
                            onClick={() => moveLayer(actualIdx, 'UP')} // Visual Down is Array Start (Up index)
                            disabled={actualIdx === 0}
                            className="w-6 h-6 flex items-center justify-center rounded bg-black/10 hover:bg-black/20 disabled:opacity-20"
                          >
                              <i className="fa-solid fa-arrow-down text-[8px]"></i>
                          </button>
                      </div>
                  </div>
              );
          })}
          <span className="text-[8px] opacity-50 text-center mt-1 uppercase font-black">Bottom (Back)</span>
      </div>
    </div>
  );
};
